// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * L'ÂGE D'UNE STRATE, dit dans l'unité où on le compte (RQ, 19/08/2026).
 *
 * ── Ce que ça répare ────────────────────────────────────────────────────────
 *
 * Les tranches d'âge sont stockées en ANNÉES DÉCIMALES — c'est la seule unité
 * qui permet de comparer une borne pédiatrique et une borne adulte sur le même
 * axe. Mais personne ne lit « 0,02 ans » : ni le patient sur la fiche publique,
 * ni la RQ sur l'écran de paramétrage. Une semaine de vie écrite en années
 * décimales est illisible ET faussement précise.
 *
 * La règle rend donc l'âge dans l'unité que le laboratoire emploie pour en
 * parler : des JOURS avant un mois, des MOIS avant un an, des ANS ensuite.
 *
 * ── Pourquoi elle vit dans le socle partagé ────────────────────────────────
 *
 * Elle était d'abord née côté vitrine (`manuel/src/lib/intervalles.ts`), pour
 * la fiche publique et le livret imprimé. Les écrans de la Qualité, eux,
 * continuaient d'afficher « 0,02 ans » — deux formats pour la même donnée, et
 * la RQ ne relisait donc pas ce que le patient allait voir.
 *
 * ⚠️ Ne JAMAIS éditer les copies déposées dans `src/shared/` des trois
 * applications : la source est ICI, et la copie se fait par
 * `bash shared/sync.sh`.
 */

/** Un nombre sans zéro de queue, avec la virgule française : « 2,10 » annonce
 *  une précision que le laboratoire n'a jamais promise, « 2,1 » non. */
export function nombreLisible(v: number): string {
  const s = String(v);
  const net = s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
  return net.replace('.', ',');
}

/**
 * Un âge en années décimales, dit en jours, en mois ou en ans.
 *
 * ⚠️ La bascule vers les MOIS se fait dès que la valeur en vaut un une fois
 * arrondie — et non à un seuil exprimé en jours. Les tranches sont stockées
 * arrondies (0,08 an EST le premier mois) : un seuil calculé en jours rendait
 * « 29 jours » pour ce que toutes les tables pédiatriques appellent un mois.
 */
export function ageLisible(v: number): string {
  if (v === 0) return 'la naissance';
  const mois = v * 12;
  if (mois < 0.9) {
    const j = Math.round(v * 365);
    return `${j} jour${j > 1 ? 's' : ''}`;
  }
  if (v < 1) return `${Math.round(mois)} mois`;
  return `${nombreLisible(v)} an${v > 1 ? 's' : ''}`;
}

/**
 * La TRANCHE d'âge, en une expression — la forme courte, celle des écrans de
 * paramétrage et des tableaux, où la place manque.
 *
 * Rend `''` quand aucune borne d'âge n'est posée : une strate qui vaut pour
 * tous les âges ne doit pas afficher « 0–130 ans », qui se lirait comme une
 * restriction alors qu'il n'y en a aucune. C'est au tableau de dire « tous ».
 */
export function trancheAgeLisible(ageMin: number | null, ageMax: number | null): string {
  if (ageMin == null && ageMax == null) return '';
  if (ageMin != null && ageMax != null) {
    const bas = ageLisible(ageMin);
    const haut = ageLisible(ageMax);
    /* L'UNITÉ NE SE RÉPÈTE PAS quand elle est la même aux deux bornes :
       « 25 – 38 ans » et non « 25 ans – 38 ans ». Mais elle reste des DEUX
       côtés quand elle change — « 7 jours – 6 mois » perdrait son sens à être
       raccourci, et c'est précisément le cas des tranches pédiatriques. */
    const unite = (t: string) => t.replace(/^[\d,]+\s*/, '');
    return unite(bas) === unite(haut)
      ? `${bas.slice(0, bas.length - unite(bas).length).trim()} – ${haut}`
      : `${bas} – ${haut}`;
  }
  if (ageMax != null) return `jusqu’à ${ageLisible(ageMax)}`;
  return `à partir de ${ageLisible(ageMin as number)}`;
}
